import React, { useState, useRef, useEffect } from 'react';
import { Modal, Row, Col, Switch, Tooltip } from 'antd';
import {
    ArrowLeftOutlined,
} from '@ant-design/icons';
import ImageZoom from './ImageZoom';
import './AIEditorModalViewer.scss';
import './ImageZoom.scss';
import './HighResModal.scss';
import ENVIRONMENT from '../../../../environments';

const CompareSiloView = ({ ai_page, isOpen, onClose, imageData, closeAllModals }) => {
    const [darkMode, setDarkMode] = useState(false);
    const [compareImageType, setCompareImageType] = useState('silo'); // 'silo' or 'inspiration'
    const [aiImageResolution, setAiImageResolution] = useState(null);
    const [siloImageResolution, setSiloImageResolution] = useState(null);
    const [isLoadingAiResolution, setIsLoadingAiResolution] = useState(false);
    const [isLoadingSiloResolution, setIsLoadingSiloResolution] = useState(false);
    const imageZoomRef = useRef(null);
    const siloImageZoomRef = useRef(null);

    
    // Helper function to get the current render based on renderIndex
    const getCurrentRender = () => {
        if (imageData?.renders && imageData?.renderIndex !== undefined) {
            return imageData.renders[imageData.renderIndex];
        }
        return null;
    };

    // Helper function to get inspiration_image_uri from the current render
    const getInspirationImageUri = () => {
        const currentRender = getCurrentRender();
        return currentRender?.inspiration_image_uri || imageData?.inspiration_image_uri;
    };

    // Helper function to get props_image_uris from the current render
    const getPropsImageUris = () => {
        const currentRender = getCurrentRender(); 
        return currentRender?.props_image_uris || imageData?.props_image_uris;
    };

    const closeModal = () => {
        closeAllModals();
    };
    const goBack = () => {
        onClose();
    };

    const extractImageResolution = (imageUrl, setResolution, setIsLoading) => {
        if (!imageUrl) return;

        setIsLoading(true);
        const img = new Image();

        img.onload = () => {
            setResolution(`${img.naturalWidth} × ${img.naturalHeight}`);
            setIsLoading(false);
        };

        img.onerror = () => {
            setResolution('Resolution not available');
            setIsLoading(false);
        };

        img.src = imageUrl;
    };

    const getAiImageResolution = () => {
        if (aiImageResolution) {
            return aiImageResolution;
        }
        if (isLoadingAiResolution) {
            return '';
        }
        return '';
    };

    const getImageResolution = () => {
        if (siloImageResolution) {
            return siloImageResolution;
        }
        if (isLoadingSiloResolution) {
            return '';
        }
        return '';
    };

    // Reset compare type when modal opens with new imageData
    useEffect(() => {
        if (isOpen && imageData) {
            // Default to silo if available, otherwise inspiration
            if (imageData?.processed_silo || imageData?.input_image_uri) {
                setCompareImageType('silo');
            } else if (getInspirationImageUri()) {
                setCompareImageType('inspiration');
            }
            setDarkMode(false); // Reset dark mode when opening
        }
    }, [isOpen, imageData]);

    useEffect(() => {
        if (imageData?.selectedImageUrl) {
            extractImageResolution(imageData.selectedImageUrl, setAiImageResolution, setIsLoadingAiResolution);
        }
        if (getSiloImage()) {
            extractImageResolution(getSiloImage(), setSiloImageResolution, setIsLoadingSiloResolution);
        }
    }, [imageData?.selectedImageUrl, imageData?.processed_silo, imageData?.renderIndex, imageData?.renders, compareImageType, darkMode]);


    const getSiloImage = () => {
        const inspirationUri = getInspirationImageUri();
        const propsUris = getPropsImageUris();

        if (compareImageType === 'inspiration' && inspirationUri) {
            return inspirationUri.includes('https://') 
                ? inspirationUri 
                : ENVIRONMENT.getBaseURL(imageData?.platform) + inspirationUri;
        }
        
        if (compareImageType === 'silo') {
            const siloUri = imageData?.processed_silo || imageData?.input_image_uri;
            if (siloUri) {
                return siloUri.includes('https://') 
                    ? siloUri 
                    : ENVIRONMENT.getBaseURL(imageData?.platform) + siloUri;
            }
        }

        if (compareImageType === 'input_image' && imageData?.original_input_image_uri) {
            return imageData.original_input_image_uri.includes('https://')
                ? imageData.original_input_image_uri
                : ENVIRONMENT.getBaseURL(imageData?.platform) + imageData.original_input_image_uri;
        }

        if (compareImageType?.startsWith('prop_')) {
            const index = parseInt(compareImageType.split('_')[1], 10);
            const propUri = propsUris?.[index];
            if (propUri) {
                return propUri.includes('https://')
                    ? propUri
                    : ENVIRONMENT.getBaseURL(imageData?.platform) + propUri;
            }
        }

        return imageData?.src;
    };

    const hasInspirationImage = () => {
        return !!getInspirationImageUri();
    };

    const hasSiloImage = () => {
        return !!(imageData?.processed_silo || imageData?.input_image_uri);
    };

    const hasBothImages = () => {
        return hasSiloImage() && hasInspirationImage();
    };

    const getImageUrl = (uri, isFullUrl = false) => {
        if (!uri) return '';
        if (uri.includes('https://') || isFullUrl) {
            return uri;
        }
        return ENVIRONMENT.getBaseURL(imageData?.platform) + uri;
    };

    const getSiloThumbnail = () => {
        const siloUri = imageData?.processed_silo || imageData?.input_image_uri;
        if (siloUri) {
            return getImageUrl(siloUri);
        }
        return '';
    };

    const getInspirationThumbnail = () => {
        const inspirationUri = getInspirationImageUri();
        if (inspirationUri) {
            return getImageUrl(inspirationUri);
        }
        return '';
    };

    const getInputImageThumbnail = () => {
        if(imageData?.original_input_image_uri){
            return getImageUrl(imageData.original_input_image_uri);
        }
        return '';
    }

    const handleImageCardClick = (type) => {
        setCompareImageType(type);
        if (type !== 'silo') {
            setDarkMode(false); // Disable dark mode for inspiration and input image
        }
    };

    return (
        <Modal
            className='ai-image-viewer-modal compare-modal'
            visible={isOpen}
            onCancel={goBack}
            title={<span className="manrope f-14 lh-22 w-500 black-085 justify-space-between" style={{ width: '58%' }}>
                <span className='cursor-pointer' onClick={goBack}><ArrowLeftOutlined /> <span className='ml-6'>Back</span></span>
                <span className="manrope f-20 lh-28 w-500 black-085">Comparison View</span>
            </span>}
            closable={true}
            footer={[

            ]}
            centered={true}>
            <Row>
                <Col span={24} className='justify-space-between'>
                    <Col span={12} className='ai-img-box'>
                        <div className={`manrope f-14 black-085 ai-comparison-img-tag left`}>AI Image</div>
                        <div className='compare-ai-image-container'>
                            <ImageZoom
                                ref={imageZoomRef}
                                imageSrc={imageData?.selectedImageUrl}
                                className="compare-ai-image"
                            />
                            <div style={{ position: 'absolute', bottom: '24px', left: '24px' }}>
                                <div className='image-resolution-display'>
                                    <span className='manrope f-14 w-500 white'>{getAiImageResolution()}</span>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col span={12} className='ai-img-box'>
                        <div className='justify-space-between'>
                            <div className={`ai-comparison-img-tag left`}>
                                <span className='manrope f-14 black-085'>
                                    {compareImageType === 'inspiration' ? 'Inspiration Image' : compareImageType === 'input_image' ? 'Input Image' : compareImageType.startsWith('prop_') ? `Prop ${parseInt(compareImageType.split('_')[1], 10) + 1}` : 'Product Reference'}
                                </span>
                            </div>

                            <div className={`button-container-compare bg-gray`}>
                                <span className={`manrope f-12 w-500 lh-20 black-085`}>Dark mode</span>
                                <Switch
                                    className='silo-compare-switch'
                                    checked={darkMode}
                                    disabled={compareImageType !== 'silo'}
                                    onChange={(checked) => {
                                        setDarkMode(checked);
                                    }} />
                            </div>
                        </div>

                        <div className='compare-ai-image-container'>
                            <ImageZoom
                                ref={siloImageZoomRef}
                                imageSrc={getSiloImage()}
                                className={`compare-ai-image ${darkMode ? `dark-mode-bg` : ''}`}
                            />
                            <div style={{ position: 'absolute', bottom: '24px', left: '24px' }}>
                                {compareImageType === 'silo' && (
                                    <div className={`ai-comparison-img-tag ${darkMode ? 'white-bg' : 'dark-bg'}`} style={{ marginBottom: 8 }}>
                                        <span className={`manrope f-14 lh-20 w-500 ${darkMode ? 'black-14' : 'white'}`}>
                                            {ai_page ? imageData?.width_dim : imageData?.width}"W x {ai_page ? imageData?.height_dim : imageData?.height}"H x {ai_page ? imageData?.depth_dim : imageData?.depth}"D
                                        </span>
                                    </div>
                                )}
                                <div className={`ai-comparison-img-tag ${darkMode ? 'white-bg' : 'dark-bg'}`}>
                                    <span className={`manrope f-14 lh-20 w-500 ${darkMode ? 'black-14' : 'white'}`}> {getImageResolution()}</span>
                                </div>
                            </div>
                            
                            {(hasBothImages() || ((imageData?.is_edit_request || imageData?.request_type == 'edit_request') && imageData?.original_input_image_uri) || (getPropsImageUris() && getPropsImageUris().length > 0)) && (
                                <div className='compare-card-pos'>
                                    {/* Silo Image Card */}
                                    {hasSiloImage() && (
                                        <Tooltip title={<span className='manrope f-12 white'>Product Reference</span>}>
                                        <div
                                            onClick={() => handleImageCardClick('silo')}
                                            className={`compare-card-img-style ${compareImageType === 'silo' ? 'selected-img' : ''}`}
                                            onMouseEnter={(e) => {
                                                if (compareImageType !== 'silo') {
                                                    e.currentTarget.style.opacity = '0.9';
                                                    e.currentTarget.style.borderColor = '#d9d9d9';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (compareImageType !== 'silo') {
                                                    e.currentTarget.style.opacity = '0.7';
                                                    e.currentTarget.style.borderColor = '#d9d9d9';
                                                }
                                            }}
                                        >
                                            <img
                                                src={getSiloThumbnail()}
                                                alt="Product Reference"
                                                className='img-setting'
                                            />
                                        </div>
                                        </Tooltip>
                                    )}
                                    {hasInspirationImage() && 
                                    /* Inspiration/Prop Image Card */
                                    <Tooltip title={<span className='manrope f-12 white'>Inspiration Image</span>}>
                                    <div
                                        onClick={() => handleImageCardClick('inspiration')}
                                        className={`compare-card-img-style ${compareImageType === 'inspiration' ? 'selected-img' : ''}`}
                                        onMouseEnter={(e) => {
                                            if (compareImageType !== 'inspiration') {
                                                e.currentTarget.style.opacity = '0.9';
                                                e.currentTarget.style.borderColor = '#D9D9D9';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (compareImageType !== 'inspiration') {
                                                e.currentTarget.style.opacity = '0.7';
                                                e.currentTarget.style.borderColor = '#d9d9d9';
                                            }
                                        }}
                                    >
                                        <img
                                            src={getInspirationThumbnail()}
                                            alt="Inspiration/Prop Image"
                                            className='img-setting'
                                        />
                                    </div>
                                    </Tooltip>
                                    }
                                    {(imageData?.is_edit_request || imageData?.request_type == 'edit_request') && imageData?.original_input_image_uri && 
                                    <Tooltip title={<span className='manrope f-12 white'>Input Image</span>}>
                                        <div
                                            onClick={() => handleImageCardClick('input_image')}
                                            className={`compare-card-img-style ${compareImageType === 'input_image' ? 'selected-img' : ''}`}
                                            onMouseEnter={(e) => {
                                                if (compareImageType !== 'input_image') {
                                                    e.currentTarget.style.opacity = '0.9';
                                                    e.currentTarget.style.borderColor = '#D9D9D9';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (compareImageType !== 'input_image') {
                                                    e.currentTarget.style.opacity = '0.7';
                                                    e.currentTarget.style.borderColor = '#d9d9d9';
                                                }
                                            }}
                                        >
                                            <img
                                                src={getInputImageThumbnail()}
                                                alt="Inspiration/Prop Image"
                                                className='img-setting'
                                            />
                                        </div>
                                    </Tooltip>
                                }
                                {getPropsImageUris()?.map((propUri, index) => (
                                    <Tooltip key={index} title={<span className='manrope f-12 white'>{`Prop ${index + 1}`}</span>}>
                                        <div
                                            onClick={() => handleImageCardClick(`prop_${index}`)}
                                            className={`compare-card-img-style ${compareImageType === `prop_${index}` ? 'selected-img' : ''}`}
                                            onMouseEnter={(e) => {
                                                if (compareImageType !== `prop_${index}`) {
                                                    e.currentTarget.style.opacity = '0.9';
                                                    e.currentTarget.style.borderColor = '#D9D9D9';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (compareImageType !== `prop_${index}`) {
                                                    e.currentTarget.style.opacity = '0.7';
                                                    e.currentTarget.style.borderColor = '#d9d9d9';
                                                }
                                            }}
                                        >
                                            <img
                                                src={getImageUrl(propUri)}
                                                alt={`Prop ${index + 1}`}
                                                className='img-setting'
                                            />
                                        </div>
                                    </Tooltip>
                                ))}
                                </div>
                            )}
                        </div>
                    </Col>
                </Col>
            </Row>

        </Modal>
    );
};

export default CompareSiloView;
